/* eslint-disable no-unused-vars */
import { useQuery } from '@tanstack/react-query';
import { useAppUpdatesContext } from '@app/app';
import { getProductType } from '@app/app-updates-product-app';
import { UpdateSchema } from '@app/app-updates/src/schema';
import SentryLogger from '@app/sentry';

export const useUpdateDetails = (updateId: string | null) => {
  const { api } = useAppUpdatesContext();

  // Helper function to handle updateId in query functions
  const createQueryFn = <T>(fn: (id: string) => T) => {
    return updateId ? () => fn(updateId) : undefined;
  };

  // Update data query
  const updateQuery = useQuery({
    queryKey: [`/gateway/api/public/app-updates/v1/change-details/${updateId}`],
    queryFn: createQueryFn(async (id) => {
      try {
        const response = await api.get(
          `/gateway/api/public/app-updates/v1/change-details/${id}`,
        );
        const updateDetails = UpdateSchema.parse(response.data);
        return {
          ...updateDetails,
          // Map product key to display name for easier UI rendering
          productTypes: updateDetails.products
            ?.map((product) => getProductType(product))
            .filter(
              (product): product is NonNullable<typeof product> =>
                product != null,
            ),
        };
      } catch (error) {
        // Distinguish Zod schema parse failures (malformed API response) from
        // network/HTTP errors so Sentry gives clearer diagnostics.
        const isParseError =
          error instanceof Error && error.name === 'ZodError';
        SentryLogger.error(error, {
          extra: {
            updateId: id,
            action: isParseError
              ? 'parse-update-details-response'
              : 'fetch-update-details',
          },
        });
        throw error;
      }
    }),
    enabled: !!updateId,
  });

  return {
    // Data
    update: updateQuery.data,
    isUpdateLoading: updateQuery.isLoading,

    // Error states
    updateError: updateQuery.error,
    hasErrors: !!updateQuery.error,
  };
};
